import { useState, useEffect } from 'react';
import { useAuth, stopImpersonation } from '@halo/web-lib';
import { EnableWriteModeModal } from './EnableWriteModeModal';

function formatTimeRemaining(expiresAtIso: string): string {
  const diff = new Date(expiresAtIso).getTime() - Date.now();
  if (diff <= 0) return '0m';
  const minutes = Math.floor(diff / 60_000);
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${minutes}m`;
}

export function ImpersonationBanner() {
  const { user, impersonation } = useAuth();
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [, setTick] = useState(0);

  // Tick once a minute so "time remaining" stays current.
  useEffect(() => {
    if (!impersonation) return;
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, [impersonation]);

  if (!impersonation) return null;

  const isWriteMode = impersonation.writeMode;
  const bgColor = isWriteMode ? 'bg-red-600' : 'bg-amber-500';
  const timeRemaining = formatTimeRemaining(impersonation.expiresAt);

  async function handleExit() {
    try {
      await stopImpersonation();
    } finally {
      window.location.href = 'https://monitoring.growhalo.ai/stores';
    }
  }

  return (
    <>
      <div className={`${bgColor} sticky top-0 z-40 w-full px-4 py-2 text-white shadow`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="text-sm font-medium">
            IMPERSONATING <strong>{impersonation.storeName}</strong> as OWNER ·{' '}
            {timeRemaining} left · {isWriteMode ? 'WRITE MODE' : 'READ-ONLY'}
          </div>
          <div className="flex items-center gap-2">
            {!isWriteMode && (
              <button
                type="button"
                onClick={() => setShowWriteModal(true)}
                className="rounded border border-white px-3 py-1 text-sm font-medium hover:bg-white/10"
              >
                Enable write mode
              </button>
            )}
            {isWriteMode && (
              <span className="rounded bg-white/20 px-2 py-1 text-xs font-bold">
                WRITE MODE ON
              </span>
            )}
            <button
              type="button"
              onClick={handleExit}
              className="rounded bg-white px-3 py-1 text-sm font-medium text-gray-900 hover:bg-gray-100"
            >
              Exit impersonation
            </button>
          </div>
        </div>
      </div>
      {showWriteModal && (
        <EnableWriteModeModal
          storeName={impersonation.storeName}
          adminEmail={user?.email ?? ''}
          onClose={() => setShowWriteModal(false)}
          onEnabled={() => {
            setShowWriteModal(false);
            window.dispatchEvent(new CustomEvent('impersonation:refetch'));
          }}
        />
      )}
    </>
  );
}
